import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, MessageCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (!detail) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/** Shared branded shell for all auth pages (login, register, forgot, reset, activate). */
export function AuthShell({ title, subtitle, children, footer = true }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Announcement banner reuse */}
      <div className="w-full text-center text-[11px] sm:text-xs font-medium py-1.5 px-4 tracking-wide"
        style={{ background: "#d4ff3d", color: "#0a0a0a" }}>
        <strong className="uppercase tracking-widest">Novità</strong>
        <span className="mx-2">·</span>
        Corsi <strong>in aula (in presenza)</strong> o <strong>online one&#8209;to&#8209;one</strong> in sessioni da 4 ore, data personalizzabile
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-10 lg:py-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
              <span className="font-bebas text-black text-xl leading-none">MDA</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-semibold text-white text-base tracking-tight">
                MyDigitalAction<span style={{ color: "#d4ff3d" }}>.</span>
              </span>
              <span className="font-mono-label text-[9px] mt-1" style={{ color: "#d4ff3d", letterSpacing: "0.25em" }}>
                EXPERIENCE · BY DIGITAL FORMULA
              </span>
            </div>
          </Link>

          <h1 className="font-display text-3xl lg:text-4xl text-white text-center">{title}</h1>
          {subtitle && <p className="text-white/60 text-sm text-center mt-3">{subtitle}</p>}

          <div className="mt-8 bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
            {children}
          </div>

          {footer && (
            <div className="mt-8 bg-card border border-white/10 rounded-2xl p-5">
              <div className="text-white/60 text-xs uppercase tracking-widest font-mono mb-3">Hai bisogno di aiuto?</div>
              <div className="space-y-2">
                <a
                  href="https://api.whatsapp.com/send?phone=393204459052&text=Ciao,%20ho%20bisogno%20di%20assistenza%20con%20l'Area%20Studente"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 text-sm text-white/85 hover:text-white"
                  data-testid="auth-help-whatsapp"
                >
                  <MessageCircle size={16} style={{ color: "#25D366" }} />
                  <span>WhatsApp: <strong>+39 320 445 9052</strong></span>
                </a>
                <a
                  href="mailto:frank@mydigitalaction.it"
                  className="flex items-center gap-3 text-sm text-white/85 hover:text-white"
                  data-testid="auth-help-email"
                >
                  <Mail size={16} style={{ color: "#d4ff3d" }} />
                  <span>frank@mydigitalaction.it</span>
                </a>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="text-white/40 hover:text-white text-xs">← Torna al sito</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Login ─────────────── */
export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/area-studente", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Accedi all'Area Studente" subtitle="Materiali corsi, onboarding, prossime date e certificati.">
      <form onSubmit={submit} data-testid="login-form">
        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="tuoindirizzo@esempio.it"
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
          data-testid="login-email" autoComplete="email" />

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="login-password" autoComplete="current-password" />

        {error && <div className="mt-4 text-red-400 text-sm" data-testid="login-error">{error}</div>}

        <button type="submit" disabled={loading}
          className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="login-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Accedi
        </button>

        <div className="mt-6 flex items-center justify-between text-xs">
          <Link to="/area-studente/registrati" className="text-white/60 hover:text-white" data-testid="link-register">
            Non hai un account? <span className="underline" style={{ color: "#d4ff3d" }}>Registrati</span>
          </Link>
          <Link to="/area-studente/password-dimenticata" className="text-white/60 hover:text-white" data-testid="link-forgot">
            Password dimenticata?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

/* ─────────────── Register ─────────────── */
export function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
      navigate("/area-studente", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Registrati" subtitle="Solo per chi ha già acquistato un corso. Usa l'email dell'ordine.">
      <form onSubmit={submit} data-testid="register-form">
        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Nome</label>
        <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Mario Rossi"
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
          data-testid="register-name" />

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Email dell'ordine</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="register-email" autoComplete="email" />

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Password (min 8 caratteri)</label>
        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="register-password" autoComplete="new-password" />

        {error && <div className="mt-4 text-red-400 text-sm" data-testid="register-error">{error}</div>}

        <button type="submit" disabled={loading}
          className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="register-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Crea account
        </button>

        <div className="mt-6 text-center text-xs">
          <Link to="/area-studente" className="text-white/60 hover:text-white">
            Hai già un account? <span className="underline" style={{ color: "#d4ff3d" }}>Accedi</span>
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

/* ─────────────── Forgot Password ─────────────── */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (_) { setSent(true); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <AuthShell title="Controlla la tua email">
        <p className="text-white/70 text-sm text-center" data-testid="forgot-sent">
          Se l'email <strong>{email}</strong> è associata a un account, riceverai le istruzioni per reimpostare la password entro pochi minuti.
        </p>
        <Link to="/area-studente" className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2">
          Torna al login <ArrowRight size={14} />
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Password dimenticata" subtitle="Inserisci l'email e ti mandiamo un link per reimpostare la password.">
      <form onSubmit={submit} data-testid="forgot-form">
        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="forgot-email" autoComplete="email" />
        <button type="submit" disabled={loading}
          className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="forgot-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
          Invia link di reset
        </button>
        <div className="mt-6 text-center text-xs">
          <Link to="/area-studente" className="text-white/60 hover:text-white">← Torna al login</Link>
        </div>
      </form>
    </AuthShell>
  );
}

/* ─────────────── Reset Password (via token) ─────────────── */
export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) { setError("Le password non coincidono"); return; }
    if (password.length < 8) { setError("Minimo 8 caratteri"); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      navigate("/area-studente", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reimposta la password">
      <form onSubmit={submit} data-testid="reset-form">
        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Nuova password (min 8 caratteri)</label>
        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="reset-password" autoComplete="new-password" />

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Conferma password</label>
        <input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="reset-confirm" autoComplete="new-password" />

        {error && <div className="mt-4 text-red-400 text-sm" data-testid="reset-error">{error}</div>}

        <button type="submit" disabled={loading || !token}
          className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="reset-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Reimposta e accedi
        </button>
        {!token && <div className="mt-4 text-yellow-400 text-xs">Link non valido. Richiedi un nuovo reset.</div>}
      </form>
    </AuthShell>
  );
}

/* ─────────────── Activate (post-purchase token) ─────────────── */
export function ActivatePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Minimo 8 caratteri"); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/activate`, { token, password, name }, { withCredentials: true });
      navigate("/area-studente", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Attiva la tua Area Studente" subtitle="Scegli una password per completare la registrazione.">
      <form onSubmit={submit} data-testid="activate-form">
        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Nome</label>
        <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Mario Rossi"
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
          data-testid="activate-name" />

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Password (min 8 caratteri)</label>
        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
          data-testid="activate-password" autoComplete="new-password" />

        {error && <div className="mt-4 text-red-400 text-sm" data-testid="activate-error">{error}</div>}

        <button type="submit" disabled={loading || !token}
          className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="activate-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Attiva e accedi
        </button>
        {!token && <div className="mt-4 text-yellow-400 text-xs">Link non valido. Contattaci per un nuovo link di attivazione.</div>}
      </form>
    </AuthShell>
  );
}
